<template>
  <div class="landing-page">
    <section class="hero-section">
      <div class="hero-copy">
        <div class="hero-layout">
          <div class="hero-lead">
            <p class="eyebrow">Independent software studio</p>
            <h1>GAB16 Software is an independent studio focused on practical software projects.</h1>
          </div>
          <div class="hero-support">
            <p class="hero-text">
              GAB16 Software is the home for ongoing software work, including web products, tools that may expand toward
              mobile, and occasional game-related ideas. ATSHelper.com is the main public project at the moment, with room
              for additional products over time.
            </p>
            <div class="hero-actions">
              <a href="https://atshelper.com" target="_blank" rel="noopener noreferrer" class="btn btn-primary hero-button">
                Visit ATSHelper
              </a>
              <router-link to="/contact" class="btn btn-outline-primary hero-button secondary-button">
                Start a conversation
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="hero-panel-section">
      <aside class="hero-panel">
        <div class="hero-panel-copy">
        <p class="panel-label">Current focus</p>
        <h2>Small team, hands-on work, and steady iteration.</h2>
        <p>
          This is a small operation, with direct involvement in both the product and technical sides of the work. That
          makes it easier to stay practical, keep decisions grounded, and improve things incrementally.
        </p>
        </div>

        <ul class="signal-list">
          <li v-for="signal in signals" :key="signal.label" class="signal-item">
            <a
              v-if="signal.link"
              :href="signal.link"
              target="_blank"
              rel="noopener noreferrer"
              class="signal-link"
            >
              <strong class="signal-value">{{ signal.value }}</strong>
            </a>
            <strong v-else class="signal-value">{{ signal.value }}</strong>
            <span class="signal-label">{{ signal.label }}</span>
          </li>
        </ul>
      </aside>
    </section>

    <section class="section-block">
      <div class="section-heading">
        <p class="section-kicker">What we do</p>
        <h3>Software projects with a practical scope and a product mindset.</h3>
      </div>
      <div class="service-grid">
        <article v-for="service in services" :key="service.title" class="service-item">
          <p class="service-tag">{{ service.tag }}</p>
          <h4>{{ service.title }}</h4>
          <p>{{ service.description }}</p>
        </article>
      </div>
    </section>

    <section class="section-block feature-section">
      <div class="feature-copy">
        <p class="section-kicker">Featured product</p>
        <h3>ATSHelper.com is the main public product today.</h3>
        <p>
          ATSHelper helps improve resumes with ATS-aware structure and guidance. It reflects the kind of software this
          studio is interested in building: useful, focused, and straightforward to use.
        </p>
        <p>
          Over time, this site can also serve as a home for additional products, technical notes, and selected
          experiments.
        </p>
      </div>

      <div class="feature-panel">
        <h4>Why it matters</h4>
        <ul class="feature-list">
          <li>The work is meant to solve specific problems rather than make broad promises.</li>
          <li>Technical choices are made with maintainability and simplicity in mind.</li>
          <li>The site leaves room for the company profile to grow in a natural way.</li>
        </ul>
      </div>
    </section>

    <section class="section-block">
      <div class="section-heading border-top-gray">
        <p class="section-kicker">Capabilities</p>
        <h3>Current work is supported by a stack suited to shipping and improving products over time.</h3>
      </div>
      <div class="cards-scroll-container">
        <div class="tech-card" v-for="technology in cards" :key="technology.title">
          <a :href="technology.link" target="_blank" rel="noopener noreferrer">
            <div class="tech-info">
              <h5>{{ technology.title }}</h5>
              <p>{{ technology.description }}</p>
            </div>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "LandingPage",
  data() {
    return {
      signals: [
        { value: "ATSHelper.com", label: "Main public project today", link: "https://atshelper.com" },
        { value: "Small team", label: "Direct involvement in product and engineering" },
        { value: "Flexible scope", label: "Web, mobile-minded, game-related work, and a touch of AI" },
      ],
      services: [
        {
          tag: "Product development",
          title: "Web platforms and software products",
          description:
            "Browser-based products and supporting tools built with an emphasis on clarity, maintainability, and steady iteration.",
        },
        {
          tag: "Mobile-ready thinking",
          title: "Apps designed to extend beyond desktop",
          description:
            "Projects are approached with cross-device usability in mind so they can evolve into mobile-friendly experiences when it makes sense.",
        },
        {
          tag: "Interactive experiments",
          title: "Game and simulation-oriented concepts",
          description:
            "There is also room for smaller interactive experiments, including game-related concepts that may develop further over time.",
        },
      ],
      cards: [
        {
          title: "Microsoft Azure",
          link: "https://azure.microsoft.com",
          description: "Cloud platform for deploying and managing web apps, pipelines, service buses and much more",
        },
        {
          title: "Vue.js",
          link: "https://vuejs.org",
          description: "A progressive JavaScript framework for building user interfaces which allows for high development velocity",
        },
        {
          title: ".NET",
          link: "https://dotnet.microsoft.com",
          description: "A developer platform for building all types of applications. Tried and tested by many companies of all sizes",
        },
        {
          title: "MS SQL Server",
          link: "https://www.microsoft.com/sql-server",
          description: "A relational database system by Microsoft",
        },
        {
          title: "GPT-OSS",
          link: "https://github.com/openai/gpt-oss",
          description: "Open local AI models that can support experimentation, automation, and product workflows",
        },
        {
          title: "Cloudflare",
          link: "https://www.cloudflare.com",
          description: "A web infrastructure platform used for DNS, caching, performance, and protection",
        },
        {
          title: "Godot Engine",
          link: "https://godotengine.org/",
          description: "An open-source game engine for 2D and 3D games, building cross-platform exports",
        },
        {
          title: "PostgreSQL",
          link: "https://www.postgresql.org/",
          description: "Alternative relational database system that is open-source",
        },
      ],
    };
  },
};
</script>

<style scoped>
.landing-page {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.hero-section {
  margin: -32px -32px 0;
}

.hero-copy,
.hero-panel {
  border: 1px solid rgba(0, 0, 105, 0.14);
  box-shadow: 0 10px 24px rgba(0, 0, 105, 0.1);
}

.hero-copy {
  padding: 42px 64px 40px;
  background:
    radial-gradient(circle at top right, rgba(105, 202, 202, 0.22), transparent 35%),
    linear-gradient(135deg, rgba(0, 0, 105, 0.92), rgba(6, 144, 105, 0.88));
  color: #f8ffff;
  margin: -1px;
  border-radius: 12px 12px 0 0;
}

.hero-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 44px;
  align-items: end;
}

.hero-lead,
.hero-support {
  display: flex;
  flex-direction: column;
}

.hero-support {
  justify-content: flex-end;
}

.eyebrow,
.section-kicker,
.panel-label,
.service-tag {
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.75rem;
  font-weight: 700;
}

.eyebrow,
.panel-label {
  color: rgba(202, 202, 250, 0.9);
  margin-bottom: 12px;
}

.hero-copy h1 {
  font-size: clamp(2.4rem, 4.6vw, 4.4rem);
  line-height: 1.02;
  margin-bottom: 0;
  max-width: none;
}

.hero-text {
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: none;
  color: rgba(244, 251, 255, 0.94);
}

.hero-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 26px;
}

.hero-button {
  border-radius: 8px;
  padding: 0.8rem 1.3rem;
  font-weight: 700;
}

.hero-copy .btn-primary {
  background-color: #069069;
  border-color: #069069;
}

.hero-copy .btn-primary:hover {
  background-color: #006900;
  border-color: #006900;
}

.secondary-button {
  background-color: rgba(202, 202, 250, 0.06);
  border-color: rgba(202, 202, 250, 0.52);
  color: #f4fbff;
}

.secondary-button:hover {
  background-color: rgba(202, 202, 250, 0.14);
  border-color: rgba(202, 202, 250, 0.8);
  color: #ffffff;
}

.hero-panel {
  padding: 30px 32px;
  background: #69caca12;
}

.hero-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
  gap: 24px;
  align-items: start;
  border-radius: 0;
  border-left: 0;
  border-right: 0;
}

.hero-panel-copy {
  max-width: 720px;
}

.hero-panel-section {
  margin: -32px -32px 0;
}

.hero-panel h2,
.section-heading h3,
.feature-copy h3 {
  margin-bottom: 12px;
  color: #000069;
}

.hero-panel p,
.service-item p,
.feature-copy p,
.feature-list li {
  color: #264e69;
  line-height: 1.7;
}

.signal-list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
}

.signal-item {
  padding: 0 0 10px;
  border-bottom: 1px solid rgba(108, 202, 169, 0.35);
}

.signal-value {
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  color: #000069;
}

.signal-link {
  text-decoration: none;
}

.signal-link:hover .signal-value {
  text-decoration: underline;
}

.signal-label {
  display: block;
  margin-top: 4px;
  color: #3d6476;
  font-size: 0.94rem;
}

.section-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-kicker,
.service-tag {
  color: #069;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  padding-top: 4px;
}

.service-item {
  padding-right: 14px;
  border-right: 1px solid rgba(108, 202, 169, 0.35);
}

.service-item h4,
.feature-panel h4 {
  margin: 10px 0 12px;
  color: #000069;
}

.feature-section {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.9fr);
  gap: 18px;
  align-items: start;
  padding-top: 8px;
  border-top: 1px solid rgba(105, 202, 202, 0.5);
}

.feature-copy {
  padding: 0 6px 0 0;
}

.feature-panel {
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

.feature-list {
  list-style: disc;
  padding-left: 20px;
  margin: 0;
}

.feature-list li + li {
  margin-top: 10px;
}

.border-top-gray {
  border-top: 1px solid rgba(105, 202, 202, 0.5);
}

@media (max-width: 992px) {
  .hero-layout,
  .feature-section,
  .service-grid {
    grid-template-columns: 1fr;
  }

  .hero-panel {
    grid-template-columns: 1fr;
  }

  .service-item {
    padding-right: 0;
    padding-bottom: 16px;
    border-right: 0;
    border-bottom: 1px solid rgba(108, 202, 169, 0.35);
  }
}

@media (max-width: 600px) {
  .hero-copy,
  .hero-panel {
    padding: 20px;
  }

  .hero-section {
    margin: -20px -14px 0;
  }

  .hero-copy {
    border-radius: 12px 12px 0 0;
  }

  .hero-panel-section {
    margin: -32px -14px 0;
  }

  .hero-copy h1 {
    max-width: none;
    font-size: 2rem;
  }

  .hero-text {
    font-size: 1rem;
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-button {
    width: 100%;
  }
}
</style>
